// Top level libraries and 3rd party dependencies
import React, { Component } from 'react';
import { func, object, string } from 'prop-types';
import { contains, isEmpty } from 'ramda';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'react-feather';

import Layout from '../Layout';
import LoadingState from '../LoadingState';
import { PIN_LENGTH, MAX_RETRY_COUNT, DEFAULT_HIPAA_SAFE_LABEL } from '../../constants/AppConstants';
import ErrorMessage from '../ErrorMessage';
import * as SessionTypes from '../../constants/ActionTypes';
import Footer from '../Footer';

// Page styles
import cx from 'classnames';
import s from './Login.css';


export default class Login extends Component {
    static propTypes = {
        conversationDetails: object.isRequired,
        conversationHash: string.isRequired,
        forgotPIN: func.isRequired,
        loadingState: object,
        login: func.isRequired,
        resetForgottenPIN: func.isRequired,
        session: object.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            PIN: '',
        };
    }

    // @TODO keeping this dumb af to get it out the door - we can revisit getting this information from the conversationDetails later.
    // @TODO I also realize I could use the environment api to tell me location, but since we're strictly using the window object it's all we need for now.
    handlePartnerLogo = () => {
        const url = window.location.href;
        const urlStrings = {
            sharecare: 'sharecaretelehealth',
            // localhost: 'localhost',
            qa: 'qa.sharecaretelehealth'
        };

        // Prod - specific to 1 emr for now
        if (contains(urlStrings.sharecare, url)) {
            return urlStrings.sharecare;
        }

        // QA - specific to 1 emr for now
        if (contains(urlStrings.qa, url)) {
            return urlStrings.sharecare;
        }

        // Local - specific to 1 emr for now
        // if (contains(urlStrings.localhost, url)) {
        //     return urlStrings.sharecare;
        // }
    };

    // enforce 5 as maxLength with slice()
    handleChangePIN = event => this.setState({[event.target.name]: event.target.value.slice(0, PIN_LENGTH)});

    /**
     * input type=number allows for floating point numbers! 4e+3E is valid. We use this to prevent + = . e and number pad .
     * http://keycode.info/
     */
    handleFloatingPointKeys = event => {
        let keyCode =  event.keyCode;

        const isNotWanted = (keyCode === 69 || keyCode === 101 || keyCode === 187 || keyCode === 189 ||keyCode === 190 || keyCode === 110 || keyCode === 107 || keyCode === 109);

        if (isNotWanted) event.preventDefault();
    };

    // Tests will fail if we do not give this Ramda method what it expects, an array. this.props.loadingState.requests returns an array.
    isLoadingLogin = loadingState => contains(SessionTypes.LOGIN_REQUEST, loadingState.requests || []);

    handleLogin = e => {
        e.preventDefault();

        const { conversationHash, login } = this.props;
        const { PIN } = this.state;

        if (this.isValidPIN()) {
            const request = {
                conversationHash,
                PIN,
            };

            login(request);
            e.target.blur();
        }
    };

    isValidPIN = () => {
        const { PIN } = this.state;

        return PIN.length === PIN_LENGTH;
    };

    render() {
        const {
            conversationHash,
            conversationDetails,
            forgotPIN,
            resetForgottenPIN,
            session,
        } = this.props;

        const { PIN } = this.state;

        const { expiredConversation } = conversationDetails;

        const hipaaSafePracticeName = `${conversationDetails.practiceName}` === DEFAULT_HIPAA_SAFE_LABEL
            ? `your ` + DEFAULT_HIPAA_SAFE_LABEL : `${conversationDetails.practiceName}`;
        const hasConversationDetails = !isEmpty(conversationDetails);
        const lockedOut = session.error ? session.error.retryCount >= MAX_RETRY_COUNT : false;
        const additionalLockedOutCopy = `${conversationDetails.practiceName}` === DEFAULT_HIPAA_SAFE_LABEL
            ? `Your ${conversationDetails.practiceName} has been notified of the failure to authenticate.`
            : `${conversationDetails.practiceName} has been notified of the failure to authenticate.`;
        const additionalPracticePhoneCopy = conversationDetails.practicePhone ? ` at ${conversationDetails.practicePhone}.` : '.';
        const isValidPIN = this.isValidPIN();
        const isLoadingLogin = this.isLoadingLogin(this.props.loadingState);
        const PINMessage = session ? session.message : 'There was a problem saving your PIN. Please contact the Practice.';

        return (
            <Layout centered className={s.layout}>
                <form
                    className={cx(s.component, session.error ? 'hasErrors' : '')}
                    id="login"
                    onSubmit={this.handleLogin}
                >
                    {hasConversationDetails && !lockedOut &&
                        <div id="conversationDetails" className={cx(s.alignItems, s.componentBlock, s.verticalSpacing, s.heading)}>
                            <h3>{conversationDetails.practiceName}</h3>
                            <label>{conversationDetails.practicePhone}</label>
                        </div>
                    }

                    {hasConversationDetails && expiredConversation && conversationDetails.hasConfirmedPIN &&
                        <ErrorMessage
                            title="This conversation has expired"
                            message={
                                conversationDetails.canReply ? `You may still send a message to the practice with the button below. If you have any immediate concerns, please contact ${hipaaSafePracticeName}${additionalPracticePhoneCopy}`
                                    : `If you have any immediate concerns, please contact ${hipaaSafePracticeName}${additionalPracticePhoneCopy}`
                            }
                        />
                    }

                    {/* Anyone who has NOT set a PIN can not reply from an expired conversation. Show them a helpful message. */}
                    {hasConversationDetails && expiredConversation && !conversationDetails.hasConfirmedPIN &&
                        <ErrorMessage
                            title="This conversation has expired"
                            message={`If you have any immediate concerns, please contact ${hipaaSafePracticeName}${additionalPracticePhoneCopy}`
                            }
                        />
                    }

                    {/* You should only see this button if canReply is true, they have confirmed a PIN, and the conversation has expired. */}
                    {expiredConversation && conversationDetails.canReply && conversationDetails.hasConfirmedPIN &&
                        <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing)}>
                            <Link
                                id="sendMessageToPractice"
                                className={cx(s.btn, s.primary, s.link)}
                                to={`/expired/${conversationHash}`}
                            >
                                <span className={s.btnText}>Send Message</span>
                            </Link>
                        </div>
                    }

                    {!lockedOut && !expiredConversation &&
                        <React.Fragment>
                            <hr className={s.divider}/>

                            <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing, s.heading)}>
                                {PINMessage &&
                                    <div>
                                        <CheckCircle className={s.icon} size={25} />
                                        <p>{PINMessage}</p>
                                        <br />
                                    </div>
                                }
                                <p>Please enter your five-digit PIN number.</p>
                            </div>
                        </React.Fragment>
                    }

                    {!lockedOut && !expiredConversation && hasConversationDetails &&
                        <div className={cx('formGroup', s.verticalSpacing)}>
                            <div className={cx(s.componentBlock, 'fullWidth')}>
                                <input
                                    id="PIN"
                                    name="PIN"
                                    type="number"
                                    pattern="[0-9]*"
                                    className={cx('inputControl', s.bigInput, s.fauxPassword)}
                                    maxLength="5"
                                    onChange={this.handleChangePIN}
                                    onKeyDown={this.handleFloatingPointKeys}
                                    value={PIN}
                                    required
                                />
                                <label
                                    htmlFor="PIN"
                                    className={s.heading}
                                >
                                    <span>PIN</span>
                                </label>
                            </div>
                        </div>
                    }

                    {session.error && hasConversationDetails &&
                        <ErrorMessage
                            title="Failure to authenticate"
                            message={`${session.error.message} ${lockedOut ? additionalLockedOutCopy : ''} If you have any questions or concerns, please contact ${hipaaSafePracticeName}${additionalPracticePhoneCopy}`}
                        />
                    }

                    {!lockedOut && !expiredConversation && hasConversationDetails &&
                        <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing)}>
                            <button
                                className={cx(s.btn, s.primary)}
                                id="submitLogin"
                                type="submit"
                                disabled={!isValidPIN || isLoadingLogin}
                            >
                                {isLoadingLogin ?
                                    <LoadingState context="loginForm"/> :
                                    <span className={s.btnText}>View Secure Message</span>
                                }
                            </button>
                        </div>
                    }

                    {!expiredConversation && hasConversationDetails && !lockedOut && !conversationDetails.useNameVerify &&
                        <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing)}>
                            <Link
                                className={s.link}
                                to={`/forgot/${conversationHash}`}
                                onClick={forgotPIN}
                            >
                                Forgot PIN?
                            </Link>
                        </div>
                    }

                    {/* useNameVerify informs the UI that this is the Contact workflow - we call an API to reset their PIN onClick */}
                    {!expiredConversation && hasConversationDetails && !lockedOut && conversationDetails.hasConfirmedPIN && conversationDetails.useNameVerify &&
                        <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing)}>
                            <Link
                                className={s.link}
                                onClick={() => resetForgottenPIN(conversationHash)}
                                to={`/verify/${conversationHash}`}
                            >
                                Forgot PIN? Click here to reset it and create a new one.
                            </Link>
                        </div>
                    }

                    <Footer
                        partnerLogoUrl={this.handlePartnerLogo()}
                    />
                </form>
            </Layout>
        );
    }
}
